import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { organizationApi } from '@/api/services/organization.api';
import { setAccessToken } from '@/api/token';

/**
 * Move the session into another organization: store its access token (the
 * refresh cookie was already set by the response), drop every cached read of
 * the old org, and reload at the app root. Shared by the org switcher and the
 * "Create demo workspace" action, which both answer with a fresh token.
 */
export function enterOrganization(queryClient: QueryClient, accessToken: string): void {
    setAccessToken(accessToken);
    queryClient.clear();
    window.location.replace('/');
}

export function useSwitchOrganization() {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (organizationId: string) => organizationApi.switch(organizationId),
        onSuccess: (data) => {
            toast.success(t('organization.toasts.switchedSuccess'));
            enterOrganization(queryClient, data.accessToken);
        },
        onError: () => {
            toast.error(t('organization.toasts.switchedError'));
        },
    });
}
